import Select from "react-select";
import { LuPlus, LuTrash2 } from "react-icons/lu";
import { serviceLabel, TAXONOMY_MISMATCH } from "./serviceCatalog.js";
import RuleLines from "./RuleLines.jsx";
import { CodeListField } from "./CodeField.jsx";

export const emptyForm = () => ({
    name: "",
    cptCodes: "",
    cptRanges: [],
    hcpcsCodes: "",
    hcpcsRanges: [],
    revenueCodes: "",
    revenueRanges: [],
    taxonomyCodes: "",
    typeOfBill: "",
    placesOfService: "",
    maxDailyUnits: "",
    countsTowardOutOfPocket: "",
    taxonomyMismatch: "",
    rules: [],
    humanDescription: "",
});

export const listText = (codes) => (codes || []).join(", ");

const splitList = (text) => String(text || "").split(",").map((item) => item.trim()).filter(Boolean);

const rangeRows = (groups) => (groups || []).map((group) => ({
    name: group.name || "",
    from: group.from || "",
    to: group.to || "",
}));

const splitRanges = (ranges, key, suffix) => {
    const used = new Set();
    return (ranges || []).flatMap((range) => {
        const from = String(range.from || "").trim();
        const to = String(range.to || "").trim();
        if (!from && !to) return [];
        let name = String(range.name || "").trim();
        if (!name || used.has(name)) {
            let index = used.size + 1;
            name = `${key}${suffix}${index}`;
            while (used.has(name)) {
                index += 1;
                name = `${key}${suffix}${index}`;
            }
        }
        used.add(name);
        return [{ name, from, to }];
    });
};

export const camelKey = (label) => {
    const parts = String(label || "").trim().split(/[^A-Za-z0-9]+/).filter(Boolean);
    return parts.map((part, index) => (
        index === 0 ? part.charAt(0).toLowerCase() + part.slice(1) : part.charAt(0).toUpperCase() + part.slice(1)
    )).join("");
};

const YES_NO = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
];

export const formFromService = (service) => ({
    name: serviceLabel(service.key),
    cptCodes: listText(service.cptCodes),
    cptRanges: rangeRows(service.cptCodeGroups),
    hcpcsCodes: listText(service.hcpcsCodes),
    hcpcsRanges: rangeRows(service.hcpcsCodeGroups),
    revenueCodes: listText(service.revenueCode),
    revenueRanges: rangeRows(service.revenueCodeGroups),
    taxonomyCodes: listText(service.taxonomyCodes),
    typeOfBill: listText(service.typeOfBill),
    placesOfService: listText(service.placesOfService),
    maxDailyUnits: service.maxDailyUnits == null ? "" : String(service.maxDailyUnits),
    countsTowardOutOfPocket: service.countsTowardOutOfPocket == null ? "" : (service.countsTowardOutOfPocket ? "yes" : "no"),
    taxonomyMismatch: service.taxonomyMismatch || "",
    rules: structuredClone(service.rules || []),
    humanDescription: service.humanDescription || "",
});

export const serviceFromForm = (form, key) => {
    const service = { key };
    const lists = {
        cptCodes: splitList(form.cptCodes),
        cptCodeGroups: splitRanges(form.cptRanges, key, "CptCodes"),
        hcpcsCodes: splitList(form.hcpcsCodes),
        hcpcsCodeGroups: splitRanges(form.hcpcsRanges, key, "HcpcsCodes"),
        revenueCode: splitList(form.revenueCodes),
        revenueCodeGroups: splitRanges(form.revenueRanges, key, "RevenueCodes"),
        placesOfService: splitList(form.placesOfService),
        typeOfBill: splitList(form.typeOfBill),
        taxonomyCodes: splitList(form.taxonomyCodes),
    };
    Object.entries(lists).forEach(([field, value]) => {
        if (value.length) service[field] = value;
    });
    const units = Number(String(form.maxDailyUnits || "").replace(/[^\d.]/g, ""));
    if (String(form.maxDailyUnits || "").trim() !== "" && !Number.isNaN(units)) service.maxDailyUnits = units;
    if (form.countsTowardOutOfPocket) service.countsTowardOutOfPocket = form.countsTowardOutOfPocket === "yes";
    if (form.taxonomyMismatch) service.taxonomyMismatch = form.taxonomyMismatch;
    const rules = (form.rules || []).filter((rule) => rule.when || rule.then);
    if (rules.length) service.rules = rules;
    const description = String(form.humanDescription || "").trim();
    if (description) service.humanDescription = description;
    return service;
};

const RangeList = ({ label, ranges, onChange, from, to }) => {
    const update = (index, field, value) => {
        onChange(ranges.map((range, itemIndex) => (itemIndex === index ? { ...range, [field]: value } : range)));
    };
    return (
        <div className="internal-work__field">
            <span>{label} ranges</span>
            {ranges.map((range, index) => (
                <div className="internal-work__range" key={`${range.name}-${index}`}>
                    <input
                        type="text"
                        value={range.from}
                        placeholder={from}
                        aria-label={`${label} range ${index + 1} from`}
                        onChange={(event) => update(index, "from", event.target.value)}
                    />
                    <input
                        type="text"
                        value={range.to}
                        placeholder={to}
                        aria-label={`${label} range ${index + 1} to`}
                        onChange={(event) => update(index, "to", event.target.value)}
                    />
                    <button
                        type="button"
                        className="internal-work__icon is-danger"
                        aria-label={`Remove ${label} range ${index + 1}`}
                        onClick={() => onChange(ranges.filter((_, itemIndex) => itemIndex !== index))}
                    >
                        <LuTrash2 />
                    </button>
                </div>
            ))}
            <button
                type="button"
                className="internal-work__range-add"
                onClick={() => onChange([...ranges, { name: "", from: "", to: "" }])}
            >
                <LuPlus />
                Add {label} range
            </button>
        </div>
    );
};

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 80 }),
    option: (base, state) => ({
        ...base,
        whiteSpace: "normal",
        color: "var(--darkerGrey, #333)",
        backgroundColor: state.isFocused || state.isSelected ? "#f7f7f7" : "#fff",
    }),
};

export const PickField = ({ label, value, options, onChange, placeholder, isClearable = true, isSearchable = false }) => (
    <div className="internal-work__field">
        <span>{label}</span>
        <Select
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable={isClearable}
            isSearchable={isSearchable}
            options={options}
            value={options.find((option) => option.value === value) || null}
            onChange={(option) => onChange(option ? option.value : "")}
            formatOptionLabel={(option, { context }) => (
                context === "menu" && option.description ? (
                    <span className="internal-work__menu-option">
                        <span>{option.label}</span>
                        <small>{option.description}</small>
                    </span>
                ) : option.label
            )}
            placeholder={placeholder || ""}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            styles={selectStyles}
        />
    </div>
);

export const TextField = ({ label, value, onChange, placeholder, inputRef, readOnly }) => (
    <div className="internal-work__field">
        <span>{label}</span>
        <input
            ref={inputRef}
            type="text"
            value={value}
            placeholder={placeholder}
            readOnly={readOnly}
            onChange={(event) => onChange(event.target.value)}
        />
    </div>
);

export const ServiceFields = ({ form, setField, nameRef, rulesKey, nameField, billingLocked }) => (
    <>
        <div className="internal-work__fields">
            {nameField || <TextField label="Service name" value={form.name} onChange={setField("name")} inputRef={nameRef} />}
            <CodeListField kind="cpt" label="CPT codes" value={form.cptCodes} onChange={setField("cptCodes")} placeholder="99213, 99214" />
            <RangeList label="CPT" from="99202" to="99215" ranges={form.cptRanges || []} onChange={setField("cptRanges")} />
            <CodeListField kind="hcpcs" label="HCPCS codes" value={form.hcpcsCodes} onChange={setField("hcpcsCodes")} placeholder="G0438, G0439" />
            <RangeList label="HCPCS" from="A0426" to="A0434" ranges={form.hcpcsRanges || []} onChange={setField("hcpcsRanges")} />
            <CodeListField kind="revenue" label="Revenue codes" value={form.revenueCodes} onChange={setField("revenueCodes")} placeholder="0450, 0451" />
            <RangeList label="Revenue" from="0510" to="0519" ranges={form.revenueRanges || []} onChange={setField("revenueRanges")} />
        </div>
        <h3 className="internal-work__subhead">Where it is billed</h3>
        {billingLocked ? <p className="internal-work__adj-codes">These come from the service catalog. Change them on the Services page.</p> : null}
        <div className="internal-work__fields">
            <CodeListField kind="pos" label="Places of service" value={form.placesOfService} onChange={setField("placesOfService")} placeholder={billingLocked ? "" : "11, 22"} readOnly={billingLocked} />
            <CodeListField kind="tob" label="Type of bill" value={form.typeOfBill} onChange={setField("typeOfBill")} placeholder={billingLocked ? "" : "0131, 0711"} readOnly={billingLocked} />
            <CodeListField kind="taxonomy" label="Provider types (taxonomy codes)" value={form.taxonomyCodes} onChange={setField("taxonomyCodes")} placeholder={billingLocked ? "Any provider" : "207Q00000X, 208000000X"} readOnly={billingLocked} />
            <PickField
                label="When the provider type doesn't match"
                value={form.taxonomyMismatch}
                options={TAXONOMY_MISMATCH}
                onChange={setField("taxonomyMismatch")}
                placeholder="Choose what happens"
            />
            <TextField label="Max units per day" value={form.maxDailyUnits} onChange={setField("maxDailyUnits")} placeholder="1" />
            <PickField
                label="Counts toward out-of-pocket"
                value={form.countsTowardOutOfPocket}
                options={YES_NO}
                onChange={setField("countsTowardOutOfPocket")}
            />
        </div>
        <h3 className="internal-work__subhead">Rules</h3>
        <RuleLines key={rulesKey} rules={form.rules || []} onChange={setField("rules")} />
        <div className="internal-work__field">
            <span>In plain language</span>
            <textarea
                className="internal-work__notes"
                value={form.humanDescription || ""}
                onChange={(event) => setField("humanDescription")(event.target.value)}
                aria-label="In plain language"
            />
        </div>
    </>
);
